/**
 * Feed Posts Import - Modern Grid View
 *
 * Modern 3-column Instagram-style grid for selecting feed posts.
 * Features:
 * - Fixed 3-column layout (grid grid-cols-3)
 * - Square aspect ratio thumbnails
 * - Checkbox overlay selection
 * - Primary border on selected items
 * - Loading skeleton states
 * - Empty state
 */

import { useEffect, useState, useImperativeHandle, forwardRef } from "react";
import { toast } from "react-toastify";
import { cn } from "lib/utils";
import api from "api/instagramApi";

const igApi = new api();

interface Post {
  id: string;
  thumbnailUrl?: string;
}

export interface FeedPostsImportHandle {
  startImport: () => Promise<boolean>;
  getSelectedCount: () => number;
  isImporting: () => boolean;
}

interface FeedPostsImportModernProps {
  accountId: string;
  onImported?: () => void;
  onSelectionChange?: (count: number) => void;
}

const FeedPostsImportModern = forwardRef<
  FeedPostsImportHandle,
  FeedPostsImportModernProps
>(({ accountId, onImported = () => {}, onSelectionChange = () => {} }, ref) => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [selectedPosts, setSelectedPosts] = useState<Post[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isImporting, setIsImporting] = useState(false);

  // Get posts on component load
  useEffect(() => {
    const abortController = new AbortController();

    (async () => {
      try {
        setIsLoading(true);
        const response = await igApi.getRecentPosts(accountId, 60, {
          signal: abortController.signal,
        });

        if (response && response.posts) {
          setPosts(response.posts);
        } else {
          // Handle error from server
          if (response && response.message) {
            toast.error(response.message);
          } else {
            toast.error(
              "No posts found, or you have not connected your account to Sked Insights yet. Connect your account on the Accounts page."
            );
          }
        }
      } catch (err) {
        // Ignore abort errors (expected when component unmounts or accountId changes)
        if (err instanceof Error && err.name === "AbortError") return;

        const message = err instanceof Error ? err.message : String(err);
        toast.error(`Error loading recent posts: ${message}`);
      } finally {
        setIsLoading(false);
      }
    })();

    return () => {
      abortController.abort();
    };
  }, [accountId]);

  // Notify parent of selection changes
  useEffect(() => {
    onSelectionChange(selectedPosts.length);
  }, [selectedPosts.length, onSelectionChange]);

  // Tile selection
  function toggleSelection(clickedPost: Post) {
    if (selectedPosts.find((p) => p.id === clickedPost.id)) {
      setSelectedPosts(
        selectedPosts.filter((post) => post.id !== clickedPost.id)
      );
    } else {
      setSelectedPosts([...selectedPosts, clickedPost]);
    }
  }

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    async startImport() {
      if (selectedPosts.length === 0) {
        toast.error("Select the posts to import by clicking on them first.");
        return false;
      }

      setIsImporting(true);
      try {
        const payload = { accountId, posts: selectedPosts };
        await igApi.insertRecentPosts(payload);
        onImported();
        toast.success("Instagram recent posts imported successfully!");
        return true;
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : typeof err === "object" && err !== null && "data" in err
              ? (err as any).data?.error || String(err)
              : String(err);
        toast.error(`Error importing posts: ${message}`);
        return false;
      } finally {
        setIsImporting(false);
      }
    },
    getSelectedCount() {
      return selectedPosts.length;
    },
    isImporting() {
      return isImporting;
    },
  }));

  const isSelected = (post: Post) => {
    return selectedPosts.some((p) => p.id === post.id);
  };

  return (
    <>
      {isLoading ? (
        // Loading skeleton
        <div className="grid grid-cols-3 gap-3">
          {Array.from({ length: 9 }).map((_, i) => (
            <div
              key={i}
              className="aspect-square rounded-md bg-neutral-100 animate-pulse"
            />
          ))}
        </div>
      ) : posts.length === 0 ? (
        // Empty state
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-neutral-100 flex items-center justify-center mb-4">
            <i className="fa-regular fa-image text-neutral-400 text-2xl" />
          </div>
          <h3 className="font-gotham text-base font-semibold text-neutral-900 mb-1">
            No posts found
          </h3>
          <p className="font-inter text-sm text-neutral-500">
            Try selecting a different account or connect your account on the
            Accounts page
          </p>
        </div>
      ) : (
        // Instagram grid
        <div className="grid grid-cols-3 gap-3 auto-rows-fr">
          {posts.map((post) => (
            <div
              key={post.id}
              onClick={() => toggleSelection(post)}
              className={cn(
                "relative aspect-square rounded-md overflow-hidden cursor-pointer",
                "border-2 transition-all duration-200",
                isSelected(post)
                  ? "border-blue-500 shadow-md"
                  : "border-transparent hover:shadow-md"
              )}
            >
              {/* Thumbnail */}
              <img
                src={post.thumbnailUrl || "/assets/img/video_icon.png"}
                alt="Post thumbnail"
                className="w-full h-full object-cover"
              />

              {/* Checkbox overlay */}
              <div
                className={cn(
                  "absolute top-2 right-2 w-5 h-5 rounded border-2 transition-colors duration-200",
                  "flex items-center justify-center",
                  isSelected(post)
                    ? "bg-blue-500 border-blue-500"
                    : "bg-white/90 border-white"
                )}
              >
                {isSelected(post) && (
                  <i className="fa-solid fa-check text-white text-xs" />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
});

FeedPostsImportModern.displayName = "FeedPostsImportModern";

export default FeedPostsImportModern;
